import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';

import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';
import { CustomNbUser } from '../_models/customNbUser.class';
import jwt_decode from 'jwt-decode';
import { RefreshTokenComponent } from '../refresh-token/refresh-token.component';
import { Subject } from 'rxjs';
import { Tutorial } from '../models/tutorial.model';



interface User {
  email: string;
  id: number;
  token: string;
}

const alertNotifyTime = 300; // show alert before 60 secs to notify user
@Injectable()
export class AuthService implements OnDestroy {
  public usr = new CustomNbUser();

  public userSubject: BehaviorSubject<any> = new BehaviorSubject({});
  public chatObject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public profileObj: Subject<number> = new Subject();
  public user$ = this.userSubject.asObservable();
  public user: any;
  private apiUrl = environment.backendUrlPort;
  private chatUrl = environment.backendChatUrlPort;
  private tokenSubscription = new Subscription();
  private tokenRefreshed = false;

  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*'})
  };

  constructor(private dialogService: NbDialogService, private http: HttpClient, private router: Router) {
    // console.log('auth service constructor');

    this.user$.subscribe((data) => {
      this.user = data;
      // console.log(this.user);
    });

    const usr = this.getLocalStorage('user');

    if (usr) {
      this.setUser(usr);

      const expirationTime = this.getTokenExpiration(usr);
      const now = this.dateNowInSeconds();

      if (now >= expirationTime) {
        this.removeUser();
      } else {
        this.expirationCounter(expirationTime, now);
      }
    } else {
      // console.log('User not found in localstorage');
    }
  }

  postNewMessage(roomId: string, message: any) {
    let Api:string = this.chatUrl + '/room/' + roomId + '/message';
    return this.http.post<any>(Api, message, {headers: this.getChatHeaders()});
  }

  register(user: any) {
    return this.http.post(`${environment.backendUrlPort}/users/createUser`, user, this.httpOptions);
  }

  login(email: string, password: string) {

  // const user = {
  //   id: 1,
  //   email: email,
  //   password: password,
  //   token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwiaWF0IjoxNjIwNzk5NDIwLCJleHAiOjE2MjA4ODU4MjB9.e3pERVIlaqtCdux9zbxdvvpI_s5qhbYTMt1Obj9SPmQ"
  // }
  // return of(user)
  // .pipe(
  //   map((user) => {
  //     if (user.email !== '') {
  //       let decodedToken;
  //       this.setUser(user);
  //       decodedToken = jwt_decode(this.user.token, { header: false });
  //       this.tokenRefreshed = false;
  //       this.expirationCounter(decodedToken.exp, this.dateNowInSeconds());
  //     }
  //     return user;
  //   })
  // );

    return this.http
      .post<any>(`${environment.backendUrlPort}/login`, { email, password })
      .pipe(
        map((response) => {
          if (response.email !== '') {
            let decodedToken;
            const user = {...response};
            decodedToken = jwt_decode(user.token, { header: false });
            this.tokenRefreshed = false;
            this.expirationCounter(decodedToken.exp, this.dateNowInSeconds());
          }
          return response;
        })
      );
  }

  loginUserToChat(user) {
    const id = user.id;
    let Api:string = this.chatUrl + '/login/' + id;
    return this.http.get<any>(Api)
    .pipe(
      map((response) => {
        if (response.success) {
          this.setChatToken(response.authorization);
          this.setUser(user);
        }
        return response;
      })
    );
  }

  setChatToken(decodedToken) {
    localStorage.setItem('chatToken', decodedToken);
  }

  get_all_roles(): Observable<any> {
    let Api:string = this.apiUrl + '/users/getAllRoles/';
    return this.http.get<any>(Api);
  }

  get_all_users(): Observable<any> {
    let Api:string = this.apiUrl + '/users/getUserId';
    return this.http.get<any>(Api, {headers: this.getChatHeaders()});
  }

  resetPassword(email: string) {
    return this.http.post<any>(`${environment.backendUrlPort}/forgot-password/` + email, {}).pipe();
  }

  // validate token provided in the email url
  validateToken(token: string) {
    return this.http.post<any>(`${environment.backendUrlPort}/validate-token/` + token, {});
  }

  // set user new password
  setPassword(password: string, token: string) {
    return this.http.post<any>(`${environment.backendUrlPort}/set-password/` + token, { password });
  }

  refreshToken() {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.user.token,
      }),
    };

    return this.http
      .post<any>(`${environment.backendUrlPort}/refresh-token`, {}, httpOptions)
      .pipe(catchError(this.errorHandler));
  }

  expirationCounter(expiration: number, now: number) {
    const timeout = expiration - now;
    this.tokenSubscription.unsubscribe();

    if (timeout > 0) {
      let delayTime: number;
      if (timeout > alertNotifyTime) {
        delayTime = timeout - alertNotifyTime;
      } else {
        delayTime = 1;
      }

      this.tokenSubscription = of(null)
        .pipe(delay(delayTime * 1000))
        .subscribe((expired) => {
          // add delay (expireIn) in sec, in order to notify user to refresh token
          // if timeout > alertNotify, then use alertNotifyTime, else use timeout
          const expireIn = Math.min(timeout, alertNotifyTime);
          this.openRefreshTokenDialog(expireIn);
          setTimeout(() => {
            if (!this.tokenRefreshed) {
              this.logout();
            } else {
              this.tokenRefreshed = false;
              this.expirationCounter(this.getTokenExpiration(this.user), this.dateNowInSeconds());
            }
          }, expireIn * 1000);
        });
    } else {
      // token has already expired, time has pass token expiration time
      this.logout();
    }
  }

  getTokenExpiration(user: User) {
    const decodedToken = jwt_decode(user.token, { header: false }) as any;
    return decodedToken.exp;
  }

  openRefreshTokenDialog(timeout: number) {
    this.dialogService.open(RefreshTokenComponent, {
      context: {
        timeout,
      },
      closeOnBackdropClick: false,
    });
  }

  logout(): any {
    localStorage.removeItem('user');
    localStorage.removeItem('chatToken');
    localStorage.removeItem('first_login');
    this.router.navigate(['/auth']);
  }

  getUser() {
    return localStorage.getItem('user');
  }

  removeUser() {
    localStorage.removeItem('user');
  }

  setUser(user: any) {
    this.tokenRefreshed = true;
    localStorage.setItem('user', JSON.stringify(user));
    this.userSubject.next(user);
  }

  setEmailFirstNameLastName(email, first_name, last_name) {
    const oldUser = JSON.parse(this.getUser())
    const user = {
      id: oldUser.id,
      token: oldUser.token,
      email: email,
      firstName: first_name,
      lastName: last_name
    }
    this.setUser(user);
    this.userSubject.next(user);
  }

  getLocalStorage(name: string) {
    return JSON.parse(localStorage.getItem(name));
  }

  dateNowInSeconds() {
    const nowMs = Date.now();
    return (nowMs - (nowMs % 1000)) / 1000; // convert epoch in ms to epoch in seconds
  }

  async getLocalStorageUser() {
    return localStorage.getItem('user');
  }

  isLoggedIn(): boolean {
    return this.getUser() !== null;
  }

  errorHandler(error: any[]) {
    return throwError(new Error('Backend Server Error'));
  }

  createNewRoom(data:any) {
    let Api:string = this.chatUrl + '/room/initiate';
    return this.http.post<any>(Api, data, {headers: this.getChatHeaders()})
  }

  getChatHeaders() {
    return new HttpHeaders({
        Authorization: 'Bearer ' + localStorage.getItem('chatToken').toString()
      });
  }

  get_all_user_rooms(): Observable<any> {
    let Api:string = this.chatUrl + '/room/userRooms';
    return this.http.get<any>(Api, {headers: this.getChatHeaders()});
  }

  get_all_room_messages(roomId): Observable<any> {
    let Api:string = this.chatUrl + '/room/' + roomId + '?limit=100&page=0';
    return this.http.get<any>(Api, {headers: this.getChatHeaders()});
  }

  get_room_last_message(roomId): Observable<any> {
    let Api:string = this.chatUrl + '/room/' + roomId + '?limit=1&page=0';
    return this.http.get<any>(Api, {headers: this.getChatHeaders()});
  }

  deleteWorkspace(roomId: string): Observable<any> {
    let Api:string = this.chatUrl + '/delete/room/' + roomId;
    return this.http.delete<any>(Api, {headers: this.getChatHeaders()});
  }

  ngOnDestroy() {}

}

export const AUTH_PROVIDERS: Array<any> = [{ provide: AuthService, useClass: AuthService }];
